/**
 * useVideoPlayer — encapsulates ReactPlayer interaction state.
 *
 * Extracted from EstadisticasNew.js (handleVideoProgress / handleVideoDuration /
 * handleVideoSeek / skipVideo / playbackRate / videoOffset / periodOffsets).
 *
 * Logic preserved verbatim — only the wiring is split out so a v2 screen can
 * compose this hook with the rest of the stats stack.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { DEFAULT_VIDEO_OFFSET } from '../constants';

export function useVideoPlayer({ enabled = true, onPeriodAutoChange } = {}) {
  const playerRef = useRef(null);

  const [videoUrl, setVideoUrl] = useState('');
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [videoOffset, setVideoOffset] = useState(DEFAULT_VIDEO_OFFSET);
  const [periodOffsets, setPeriodOffsets] = useState([0, 0, 0, 0]);

  // ReactPlayer onProgress callback. Also derives the current period from
  // periodOffsets so callers can sync UI without managing a second timer.
  const handleProgress = useCallback((state) => {
    setVideoCurrentTime(state.playedSeconds);

    if (periodOffsets.length > 0 && typeof onPeriodAutoChange === 'function') {
      let currentVideoPeriod = 1;
      let cumulativeTime = 0;
      for (let i = 0; i < periodOffsets.length; i++) {
        cumulativeTime += periodOffsets[i];
        if (state.playedSeconds < cumulativeTime) {
          currentVideoPeriod = i + 1;
          break;
        }
      }
      onPeriodAutoChange(currentVideoPeriod);
    }
  }, [periodOffsets, onPeriodAutoChange]);

  const handleDuration = useCallback((duration) => {
    setVideoDuration(duration);
  }, []);

  const seekTo = useCallback((time) => {
    if (playerRef.current) {
      playerRef.current.seekTo(time, 'seconds');
    }
  }, []);

  const skip = useCallback((seconds) => {
    if (playerRef.current) {
      const t = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(t + seconds, 'seconds');
    }
  }, []);

  const togglePlay = useCallback(() => {
    setVideoPlaying((p) => !p);
  }, []);

  const adjustRate = useCallback((delta) => {
    setPlaybackRate((prev) => {
      const next = prev + delta;
      if (next < 0.25) return 0.25;
      if (next > 2) return 2;
      return next;
    });
  }, []);

  // Keyboard shortcuts (only when this hook is the active screen)
  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          setVideoPlaying((prev) => !prev);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skip(e.shiftKey ? -1 : -5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skip(e.shiftKey ? 1 : 5);
          break;
        case 'Comma':
          e.preventDefault();
          setPlaybackRate((prev) => Math.max(0.25, prev - 0.25));
          break;
        case 'Period':
          e.preventDefault();
          setPlaybackRate((prev) => Math.min(2, prev + 0.25));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, skip]);

  // Stable factory consumed by useStatsSession.recordAction in video mode.
  // Mirrors recordAction's `Math.max(0, videoCurrentTime + videoOffset)`.
  const computeActionTimestamp = useCallback(() => {
    return Math.max(0, videoCurrentTime + videoOffset);
  }, [videoCurrentTime, videoOffset]);

  const reset = useCallback(() => {
    setVideoUrl('');
    setVideoPlaying(false);
    setVideoDuration(0);
    setVideoCurrentTime(0);
    setPlaybackRate(1);
    setVideoOffset(DEFAULT_VIDEO_OFFSET);
    setPeriodOffsets([0, 0, 0, 0]);
  }, []);

  return {
    // refs
    playerRef,

    // state
    videoUrl,
    setVideoUrl,
    videoPlaying,
    setVideoPlaying,
    videoDuration,
    setVideoDuration,
    videoCurrentTime,
    setVideoCurrentTime,
    playbackRate,
    setPlaybackRate,
    videoOffset,
    setVideoOffset,
    periodOffsets,
    setPeriodOffsets,

    // ReactPlayer handlers
    handleProgress,
    handleDuration,

    // Imperative video controls
    seekTo,
    skip,
    togglePlay,
    adjustRate,

    // Helpers
    computeActionTimestamp,
    reset,
  };
}
